import { apiFiles } from "@/api/file.api.ts";
import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import { FileUpload } from "@/components/files/FileUpload.tsx";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useReminderHook } from "@/hooks/use-reminder.hook.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { useTaskSave } from "@/queries/employees/use-employee-tasks.query.ts";
import useEmployeeSearchInline, { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { ApiEntityTypes, ApiFile } from "@/types/common.types.ts";
import {
  getActiveEmployeeLifecycleStatuses,
  ApiEmployeeShort,
  getEmployeeAvatarUrl,
} from "@/types/employees/employee.types.ts";
import { ApiNotificationChannelType } from "@/types/notification.types.ts";
import { ReminderMode, ReminderPeriod } from "@/types/reminder.types.ts";
import { ApiTask } from "@/types/task.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  FontAwesomeIcon,
  Input,
  ListItem,
  Modal,
  ModalProps,
  Multiselect,
  Radio,
  Select,
  Stack,
  Switch,
  Tag,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { Controller, type Resolver, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface TaskEditModalProps extends ModalProps {
  task: ApiTask | null;
  assignToEmployee?: ApiEmployeeShort;
  relatedEmployee?: ApiEmployeeShort;
}

// eslint-disable-next-line react-refresh/only-export-components
export const buildTaskSchema = (requiredError: string) =>
  z.object({
    title: z.string({ error: requiredError }).min(1, requiredError),
    description: z.string().nullable().default(null),
    date_due: z.date({ error: requiredError }).nullable().default(null),
    assign_to_employee_id: z.number({ error: requiredError }),
    related_employee_id: z.number({ error: requiredError }).nullable().default(null),
    visible_to_employee_ids: z.array(z.number({ error: requiredError })).default([]),
    reminders_enabled: z.boolean({ error: requiredError }).default(false),
    reminder_settings: z
      .object({
        channels: z
          .array(z.enum(ApiNotificationChannelType, { error: requiredError }))
          .default([
            ApiNotificationChannelType.in_app,
            ApiNotificationChannelType.email,
            ApiNotificationChannelType.slack,
          ]),
        mode: z.enum(ReminderMode, { error: requiredError }).default(ReminderMode.medium),
        custom_subject: z.string().nullable().default(null),
        custom_body: z.string().nullable().default(null),
        custom_frequency: z.number({ error: requiredError }).nullable().default(null),
        custom_period: z.enum(ReminderPeriod, { error: requiredError }).nullable().default(null),
      })
      .default({
        channels: [
          ApiNotificationChannelType.in_app,
          ApiNotificationChannelType.email,
          ApiNotificationChannelType.slack,
        ],
        mode: ReminderMode.medium,
        custom_subject: null,
        custom_body: null,
        custom_frequency: null,
        custom_period: null,
      }),
  });

type TaskSchema = z.output<ReturnType<typeof buildTaskSchema>>;

export function TaskEditModal({
  task,
  assignToEmployee,
  relatedEmployee,
  ...props
}: TaskEditModalProps) {
  const { t } = useTranslation();
  const { id } = props;
  const { close } = useModals();
  const { renderEmployeeName } = useFormatter();
  const { handleErrors } = useErrorHandle();
  const {
    getReminderModeTitle,
    getReminderModeDescription,
    getReminderSettingsDescription,
    getReminderPeriodTitle,
    getReminderEveryTitle,
    calculateReminderCount,
    getNotificationChannelTitle,
    getReminderModeTagVariant,
  } = useReminderHook();

  const isEditMode = !!task;

  const createTaskSchema = buildTaskSchema(t("errors:field_required"));

  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm<TaskSchema>({
    mode: "onSubmit",
    resolver: zodResolver(createTaskSchema) as Resolver<TaskSchema>,
    defaultValues: {
      title: task?.title ?? "",
      description: task?.description ?? null,
      date_due: task?.date_due ?? null,
      assign_to_employee_id: (task?.assign_to_employee.id ?? assignToEmployee?.id) as number,
      related_employee_id: task?.related_employee?.id ?? relatedEmployee?.id ?? null,
      visible_to_employee_ids: task?.visible_to_employees.map((e) => e.id) ?? [],
      reminders_enabled: !!task?.reminder_settings?.mode,
      reminder_settings: task?.reminder_settings?.mode
        ? task?.reminder_settings
        : {
            channels: [
              ApiNotificationChannelType.in_app,
              ApiNotificationChannelType.email,
              ApiNotificationChannelType.slack,
            ],
            mode: ReminderMode.medium,
            custom_subject: null,
            custom_body: null,
            custom_frequency: null,
            custom_period: null,
          },
    },
  });

  const dateDue = useWatch({ control, name: "date_due" });
  const remindersEnabled = useWatch({ control, name: "reminders_enabled" });
  const reminderSettings = useWatch({ control, name: "reminder_settings" });
  const [isChangingMode, setIsChangingMode] = useState(false);

  const daysUntilDeadline = useMemo(() => {
    if (!dateDue) return 0;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const due = new Date(dateDue);
    due.setHours(0, 0, 0, 0);
    return Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }, [dateDue]);

  const reminderCount = useMemo(() => {
    if (!reminderSettings?.mode) return 0;
    return calculateReminderCount(daysUntilDeadline, reminderSettings.mode);
  }, [reminderSettings?.mode, daysUntilDeadline]);

  const externalChannelOptions = [
    {
      id: ApiNotificationChannelType.email,
      title: getNotificationChannelTitle(ApiNotificationChannelType.email),
    },
    {
      id: ApiNotificationChannelType.slack,
      title: getNotificationChannelTitle(ApiNotificationChannelType.slack),
    },
    {
      id: ApiNotificationChannelType.messenger,
      title: getNotificationChannelTitle(ApiNotificationChannelType.messenger),
    },
  ];

  const taskSave = useTaskSave(control);

  const { data: employeeMe } = useEmployeeMe();

  const assignToSearch = useEmployeeSearchInline(true, {
    employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
  });
  const relatedEmployeeSearch = useEmployeeSearchInline(true, {
    employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
  });
  const visibleToSearch = useEmployeeSearchInline(true, {
    employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
    exclude_ids: employeeMe?.id ? [employeeMe.id] : [],
  });

  const [files, setFiles] = useState<ApiFile[]>(task?.files ?? []);

  function setDefaultCustomReminderSettings() {
    setValue("reminder_settings.mode", ReminderMode.custom);
    setValue("reminder_settings.custom_frequency", 1);
    setValue("reminder_settings.custom_period", ReminderPeriod.day);
  }

  function ensureCustomReminderSettings() {
    setValue("reminder_settings.mode", ReminderMode.custom);
    if (!reminderSettings.custom_frequency) {
      setValue("reminder_settings.custom_frequency", 1);
    }
    if (!reminderSettings.custom_period) {
      setValue("reminder_settings.custom_period", ReminderPeriod.day);
    }
  }

  function handleDateDueChange(
    newDate: Date | null | undefined,
    onChange: (value: Date | null | undefined) => void
  ) {
    onChange(newDate);

    if (newDate) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const due = new Date(newDate);
      due.setHours(0, 0, 0, 0);

      if (due <= now) {
        setValue("reminders_enabled", false);
        setIsChangingMode(false);
      }
      return;
    }

    if (remindersEnabled && reminderSettings.mode !== ReminderMode.custom) {
      setDefaultCustomReminderSettings();
    }
  }

  async function removeFile(file: ApiFile) {
    setFiles((prev) => prev.filter((f) => f.id !== file.id));
    const { error } = await apiFiles.delete(file.uuid);
    if (error) handleErrors(error);
  }

  const onSubmit = handleSubmit(async (data) => {
    try {
      const reminderSettings =
        data.reminders_enabled && data.reminder_settings
          ? {
              channels: data.reminder_settings.channels,
              custom_subject: data.reminder_settings.custom_subject,
              custom_body: data.reminder_settings.custom_body,
              mode: data.reminder_settings.mode,
              ...(data.reminder_settings.mode === ReminderMode.custom
                ? {
                    custom_frequency: data.reminder_settings.custom_frequency,
                    custom_period: data.reminder_settings.custom_period,
                  }
                : {}),
            }
          : null;

      await taskSave.mutateAsync(
        {
          id: task?.id,
          payload: {
            title: data.title,
            description: data.description ?? null,
            date_due: data.date_due ?? null,
            assign_to_employee_id: data.assign_to_employee_id,
            related_employee_id: data.related_employee_id ?? null,
            visible_to_employee_ids: data.visible_to_employee_ids ?? [],
            file_uuids: files.map((f) => f.uuid),
            reminder_settings: reminderSettings,
          },
        },
        {
          onSuccess: (savedTask) => {
            analyticsService.trackEvent(
              isEditMode ? analyticEvents.tasks.edited : analyticEvents.tasks.created,
              { [analyticProperties.id]: savedTask?.id }
            );
          },
        }
      );
      close(id);
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      title={isEditMode ? t("tasks:edit_task") : t("tasks:create_task")}
      size={"lg"}
      withCloser={true}
      variant={"middle"}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            size={"lg"}
            className={"w-full"}
            type={"submit"}
            onClick={onSubmit}
            isLoading={taskSave.isPending}
          >
            {isEditMode ? t("common:save") : t("tasks:create_task")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack>
          <Input
            {...register("title")}
            label={t("common:title")}
            placeholder={t("tasks:title_placeholder")}
            required
            error={errors.title?.message}
            size={"lg"}
          />

          <Controller
            control={control}
            name="assign_to_employee_id"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("common:assign_to")}
                emptyTitle={t("common:select")}
                isLoading={assignToSearch.isLoading}
                value={value}
                showCaption={true}
                showAvatar={true}
                searchable
                onSearchOptions={(text) => assignToSearch.search(text)}
                searchOptions={assignToSearch.options}
                loadOptionsOnOpen
                options={
                  isEditMode && task
                    ? [
                        {
                          id: task.assign_to_employee.id,
                          title: renderEmployeeName(task.assign_to_employee),
                          avatarUrl: getEmployeeAvatarUrl(task.assign_to_employee, "md"),
                        },
                      ]
                    : assignToEmployee
                      ? [
                          {
                            id: assignToEmployee.id,
                            title: renderEmployeeName(assignToEmployee),
                            avatarUrl: getEmployeeAvatarUrl(assignToEmployee, "md"),
                          },
                        ]
                      : []
                }
                onChange={onChange}
                error={errors.assign_to_employee_id?.message}
                required
              />
            )}
          />

          <Stack gap={"md"}>
            <Stack direction={"horizontal"} gap={"md"} items={"center"}>
              <Box className={"grow"}>
                <Controller
                  control={control}
                  name="date_due"
                  render={({ field: { value, onChange } }) => (
                    <Datepicker
                      value={value ?? undefined}
                      onChange={(newDate) => handleDateDueChange(newDate, onChange)}
                      label={t("common:due_date")}
                      isClearable
                      error={errors.date_due?.message}
                    />
                  )}
                />
              </Box>

              <Controller
                control={control}
                name="reminders_enabled"
                render={({ field: { value, onChange } }) => (
                  <Switch
                    size={"sm"}
                    checked={value}
                    disabled={!!dateDue && daysUntilDeadline <= 0}
                    onChange={(checked) => {
                      onChange(checked);
                      if (checked && dateDue) {
                        setValue("reminder_settings.mode", ReminderMode.medium);
                      }
                      if (checked && !dateDue) {
                        setDefaultCustomReminderSettings();
                      }
                      if (!checked) {
                        setIsChangingMode(false);
                      }
                    }}
                    title={t("core:turn_on_reminders")}
                    description={t("tasks:overdue_alerts_sent_anyway")}
                  />
                )}
              />
            </Stack>

            {remindersEnabled && (
              <Box className="py-4 px-6 bg-dark/5 border-dark/5 rounded-xs border">
                {!isChangingMode ? (
                  <ListItem
                    className="py-3"
                    behavior={"card"}
                    title={t("core:reminder_mode", {
                      level: getReminderModeTitle(reminderSettings.mode),
                    })}
                    captionSlot={
                      <Stack>
                        <Text>{getReminderSettingsDescription(reminderSettings)}</Text>
                        <Text className="text-secondary">
                          {reminderSettings.channels
                            .map((channel) => getNotificationChannelTitle(channel))
                            .join(", ")}
                        </Text>
                      </Stack>
                    }
                    valueSlot={
                      <Button variant="secondary" size="sm" onClick={() => setIsChangingMode(true)}>
                        {t("common:change")}
                      </Button>
                    }
                  />
                ) : (
                  <Stack>
                    <Stack gap={"md"}>
                      <Title
                        header={t("core:choose_reminder_frequency")}
                        size="xs"
                        caption={t("tasks:overdue_alerts_sent_regardless")}
                      />

                      {!dateDue && (
                        <Stack direction={"horizontal"}>
                          <Tag variant={"secondary"}>
                            <Stack direction={"horizontal"} gap={"xs"} items={"center"}>
                              <FontAwesomeIcon icon="fa-thin fa-circle-info" />
                              {t("core:reminder_requires", {
                                needed: t("core:reminder_requires_due_date"),
                              })}
                            </Stack>
                          </Tag>
                        </Stack>
                      )}

                      <Controller
                        control={control}
                        name="reminder_settings.mode"
                        render={({ field: { value, onChange } }) => (
                          <Stack gap="sm">
                            {[
                              ReminderMode.light,
                              ReminderMode.medium,
                              ReminderMode.hard,
                              ReminderMode.custom,
                            ].map((item) => (
                              <Radio
                                disabled={!dateDue && item !== ReminderMode.custom}
                                key={`radio-${item}`}
                                value={item}
                                checked={value === item}
                                onChange={() => {
                                  if (item === ReminderMode.custom) {
                                    ensureCustomReminderSettings();
                                    return;
                                  }
                                  onChange(item);
                                }}
                                title={getReminderModeTitle(item)}
                                caption={getReminderModeDescription(item)}
                              />
                            ))}

                            {value === ReminderMode.custom && (
                              <Stack
                                direction="horizontal"
                                gap="sm"
                                items="center"
                                className={"pl-7"}
                              >
                                <Text>
                                  {getReminderEveryTitle(
                                    reminderSettings.custom_period ?? ReminderPeriod.day,
                                    reminderSettings.custom_frequency ?? 1
                                  )}
                                </Text>
                                <Controller
                                  control={control}
                                  name="reminder_settings.custom_frequency"
                                  render={({
                                    field: { value: freqValue, onChange: freqOnChange },
                                  }) => (
                                    <Select
                                      variant="button"
                                      emptyTitle={t("common:period")}
                                      value={String(freqValue ?? 1)}
                                      onChange={(v) => freqOnChange(Number(v))}
                                      options={Array.from({ length: 6 }, (_, index) => ({
                                        id: String(index + 1),
                                        title: String(index + 1),
                                      }))}
                                    />
                                  )}
                                />
                                <Controller
                                  control={control}
                                  name="reminder_settings.custom_period"
                                  render={({
                                    field: { value: periodValue, onChange: periodOnChange },
                                  }) => (
                                    <Select
                                      key={`custom-period-${reminderSettings.custom_frequency ?? 1}`}
                                      variant="button"
                                      value={periodValue ?? ReminderPeriod.day}
                                      onChange={periodOnChange}
                                      emptyTitle={t("common:period")}
                                      options={[
                                        {
                                          id: ReminderPeriod.day,
                                          title: getReminderPeriodTitle(
                                            ReminderPeriod.day,
                                            reminderSettings.custom_frequency ?? 1
                                          ),
                                        },
                                        {
                                          id: ReminderPeriod.week,
                                          title: getReminderPeriodTitle(
                                            ReminderPeriod.week,
                                            reminderSettings.custom_frequency ?? 1
                                          ),
                                        },
                                        {
                                          id: ReminderPeriod.month,
                                          title: getReminderPeriodTitle(
                                            ReminderPeriod.month,
                                            reminderSettings.custom_frequency ?? 1
                                          ),
                                        },
                                      ]}
                                    />
                                  )}
                                />
                              </Stack>
                            )}
                          </Stack>
                        )}
                      />

                      {(daysUntilDeadline > 0 || reminderSettings.mode === ReminderMode.custom) && (
                        <Stack direction={"horizontal"}>
                          <Tag variant={getReminderModeTagVariant(reminderSettings.mode)}>
                            <Stack direction={"horizontal"} items={"center"} gap={"xs"}>
                              <FontAwesomeIcon icon="fa-light fa-bell" />
                              {reminderSettings.mode === ReminderMode.custom
                                ? getReminderSettingsDescription(reminderSettings)
                                : t("core:reminder_count_info", {
                                    reminders: t("plurals:reminders_count", {
                                      count: reminderCount,
                                    }),
                                    days: t("plurals:plural_day", { count: daysUntilDeadline }),
                                  })}
                            </Stack>
                          </Tag>
                        </Stack>
                      )}
                    </Stack>

                    <Title header={t("core:external_channels")} size="xs" paddingTop />

                    <Controller
                      control={control}
                      name="reminder_settings.channels"
                      render={({ field: { value, onChange } }) => (
                        <Multiselect
                          label={t("common:type")}
                          emptyTitle={t("common:select")}
                          values={
                            ((value as ApiNotificationChannelType[]) ?? []).filter(
                              (c) => c !== ApiNotificationChannelType.in_app
                            ) as string[]
                          }
                          options={externalChannelOptions}
                          onChange={(channels) =>
                            onChange([ApiNotificationChannelType.in_app, ...channels])
                          }
                        />
                      )}
                    />
                  </Stack>
                )}
              </Box>
            )}
          </Stack>

          <Controller
            control={control}
            name="related_employee_id"
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("tasks:related_employee")}
                emptyTitle={t("common:select")}
                isLoading={relatedEmployeeSearch.isLoading}
                value={value}
                showCaption={true}
                showAvatar={true}
                searchable
                isClearable
                onSearchOptions={(text) => relatedEmployeeSearch.search(text)}
                searchOptions={relatedEmployeeSearch.options}
                loadOptionsOnOpen
                options={
                  isEditMode && task?.related_employee
                    ? [
                        {
                          id: task.related_employee.id,
                          title: renderEmployeeName(task.related_employee),
                          avatarUrl: getEmployeeAvatarUrl(task.related_employee, "md"),
                        },
                      ]
                    : relatedEmployee
                      ? [
                          {
                            id: relatedEmployee.id,
                            title: renderEmployeeName(relatedEmployee),
                            avatarUrl: getEmployeeAvatarUrl(relatedEmployee, "md"),
                          },
                        ]
                      : []
                }
                onChange={onChange}
                error={errors.related_employee_id?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="visible_to_employee_ids"
            render={({ field: { value, onChange } }) => (
              <Multiselect
                label={t("tasks:watchers")}
                emptyTitle={t("common:select")}
                isLoading={visibleToSearch.isLoading}
                values={value}
                showCaption={true}
                showAvatar={true}
                searchable
                onSearchOptions={(text) => visibleToSearch.search(text)}
                searchOptions={visibleToSearch.options as { id: number; title: string }[]}
                loadOptionsOnOpen
                isClearable
                options={
                  isEditMode
                    ? (task?.visible_to_employees.map((employee) => ({
                        id: employee.id,
                        title: renderEmployeeName(employee),
                        avatarUrl: getEmployeeAvatarUrl(employee, "md"),
                      })) ?? [])
                    : []
                }
                onChange={onChange}
              />
            )}
          />

          <Box>
            <Title
              header={t("common:description")}
              size={"xs"}
              paddingTop={true}
              paddingBottom={true}
            />
            <Controller
              control={control}
              name="description"
              render={({ field: { value, onChange } }) => (
                <RichEditor
                  value={value || ""}
                  onChange={onChange}
                  error={errors.description?.message}
                  placeholder={t("tasks:description_placeholder")}
                  minHeight={100}
                />
              )}
            />
            <Stack gap={"sm"} items={"start"} className={"mt-2"}>
              {files.length > 0 && (
                <Stack gap={"sm"}>
                  {files.map((file) => (
                    <FileItem
                      key={`file${file.id}`}
                      file={file}
                      onRemove={() => removeFile(file)}
                    />
                  ))}
                </Stack>
              )}
              <Stack items={"start"}>
                <Box>
                  <FileUpload
                    entityId={employeeMe?.id}
                    entityType={ApiEntityTypes.employee}
                    afterSend={(file) => {
                      if (file) setFiles((prev) => [...prev, file]);
                    }}
                    buttonText={t("common:attach")}
                  />
                </Box>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </form>
    </Modal>
  );
}
